import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Loader2,
  ShieldCheck,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { ROLES, type Role } from "@rec/shared";
import { api } from "@/lib/api";
import { Badge, Button, Card, Skeleton ,
  ConfirmInline,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { useServerText } from "@/i18n";

/**
 * The people in this workspace.
 *
 * `canManage` and `assignableRoles` come from the API, so the UI never offers
 * an action the server would reject. That is presentation, not enforcement —
 * every rule is re-checked server-side, which is where it actually holds.
 */
export function Members() {
  const { t } = useTranslation(["settings", "common"]);
  const st = useServerText();
  const queryClient = useQueryClient();
  /** A pending role change that needs the serious warning first. */
  const [confirming, setConfirming] = useState<{ memberId: string; role: Role } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const members = useQuery({ queryKey: ["members"], queryFn: api.listMembers });

  const changeRole = useMutation({
    mutationFn: (input: { memberId: string; role: Role; confirm?: boolean }) =>
      api.changeMemberRole(input.memberId, input.role, input.confirm),
    onSuccess: () => {
      setConfirming(null);
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });

  const remove = useMutation({
    mutationFn: (input: { memberId: string; confirmDangerous?: boolean }) =>
      api.removeMember(input.memberId, input.confirmDangerous),
    onSuccess: () => {
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });

  if (members.isLoading) {
    return (
      <div className="mx-auto max-w-[720px] space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  const assignable = members.data?.assignableRoles ?? [];

  return (
    <div className="mx-auto max-w-[720px] space-y-5">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-text">{t("members.title")}</h1>
        <p className="mt-1 text-[13.5px] text-text-muted">
          {t("members.intro")}
        </p>
      </div>

      {assignable.length > 0 ? <InvitePanel assignable={assignable} /> : null}

      {/* Both mutations report here. Removal used to report nowhere at all,
          which is precisely where the last-owner and confirmation refusals
          land — the panel would stay open with no reason given. */}
      {changeRole.error || remove.error ? (
        <p
          role="alert"
          className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] text-danger"
        >
          {(changeRole.error ?? remove.error) instanceof Error
            ? st((changeRole.error ?? remove.error)!.message)
            : t("members.rejected")}
        </p>
      ) : null}

      <div className="space-y-2">
        {members.data?.members.map((member) => (
          <Card key={member.id} className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[14px] font-medium text-text">
                    {member.name || member.email}
                  </span>
                  {member.twoFactorEnabled ? (
                    <ShieldCheck size={13} className="shrink-0 text-success" />
                  ) : null}
                  {member.banned ? <Badge tone="danger">{t("members.suspended")}</Badge> : null}
                </div>
                <span className="mt-0.5 block truncate text-[12.5px] text-text-muted">
                  {member.email}
                </span>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {member.canManage ? (
                  <select
                    value={member.role}
                    onChange={(e) => {
                      const next = e.target.value as Role;
                      // Touching owner status — granting it or taking it away —
                      // gets an explicit confirmation rather than a silent save.
                      if (next === "super_admin" || member.role === "super_admin") {
                        setConfirming({ memberId: member.id, role: next });
                      } else {
                        changeRole.mutate({ memberId: member.id, role: next });
                      }
                    }}
                    className="h-8 rounded-lg border border-border bg-bg px-2 text-[13px] text-text focus:border-accent focus:outline-none"
                  >
                    <option value={member.role} disabled>
                      {t(`common:roles.${member.role}`)}
                    </option>
                    {assignable
                      .filter((r) => r !== member.role)
                      .map((role) => (
                        <option key={role} value={role}>
                          {t(`common:roles.${role}`)}
                        </option>
                      ))}
                  </select>
                ) : (
                  <Badge tone={member.role === "super_admin" ? "accent" : "neutral"}>
                    {t(`common:roles.${member.role}`)}
                  </Badge>
                )}

                {member.canManage ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    title={t("members.removeTitle")}
                    aria-label={t("members.removeAria", { email: member.email })}
                    disabled={remove.isPending}
                    // Confirmed rather than fired: this was a single icon click
                    // that removed somebody's access with no undo, while
                    // promoting them to Owner two lines away asks twice.
                    onClick={() => setRemoving(member.id)}
                  >
                    <UserMinus size={15} />
                  </Button>
                ) : null}
              </div>
            </div>

            {removing === member.id ? (
              <div className="mt-3">
                {member.role === "super_admin" ? (
                  <div className="rounded-lg border border-warning/40 bg-bg-subtle p-3.5">
                    <div className="flex items-start gap-2.5">
                      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" />
                      <div>
                        <p className="text-[13.5px] font-medium text-text">
                          {t("members.removeOwner.title")}
                        </p>
                        <p className="mt-1 text-[13px] leading-relaxed text-text-muted">
                          {t("members.removeOwner.body", { name: member.name || member.email })}
                        </p>
                        <div className="mt-3 flex gap-2">
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={remove.isPending}
                            onClick={() =>
                              remove.mutate({ memberId: member.id, confirmDangerous: true })
                            }
                          >
                            {remove.isPending ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : null}
                            {t("members.removeOwner.confirm")}
                          </Button>
                          <Button variant="secondary" size="sm" onClick={() => setRemoving(null)}>
                            {t("common:actions.cancel")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <ConfirmInline
                    question={
                      <Trans
                        t={t}
                        i18nKey="members.removeMember"
                        values={{ email: member.email }}
                        components={{ strong: <strong /> }}
                      />
                    }
                    confirmLabel={t("common:actions.remove")}
                    pending={remove.isPending}
                    onConfirm={() => remove.mutate({ memberId: member.id })}
                    onCancel={() => setRemoving(null)}
                  />
                )}
              </div>
            ) : null}

            {confirming?.memberId === member.id ? (
              <div
                className={cn(
                  "mt-4 rounded-lg border border-warning/40 bg-bg-subtle p-3.5",
                )}
              >
                <div className="flex items-start gap-2.5">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" />
                  <div>
                    <p className="text-[13.5px] font-medium text-text">
                      {confirming.role === "super_admin"
                        ? t("members.promote.title", { name: member.name || member.email })
                        : t("members.demote.title", { name: member.name || member.email })}
                    </p>
                    <p className="mt-1 text-[13px] leading-relaxed text-text-muted">
                      {confirming.role === "super_admin" ? (
                        <>{t("members.promote.body")}</>
                      ) : (
                        <>
                          {t("members.demote.body", {
                            role: t(`members.roleInSentence.${confirming.role}`),
                          })}
                        </>
                      )}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={changeRole.isPending}
                        onClick={() =>
                          changeRole.mutate({
                            memberId: member.id,
                            role: confirming.role,
                            confirm: true,
                          })
                        }
                      >
                        {changeRole.isPending ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : null}
                        {confirming.role === "super_admin"
                          ? t("members.promote.confirm")
                          : t("members.demote.confirm")}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setConfirming(null)}
                      >
                        {t("common:actions.cancel")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <h2 className="text-[13.5px] font-medium text-text">{t("members.rolesTitle")}</h2>
        <dl className="mt-3 space-y-2">
          {ROLES.map((role) => (
            <div key={role} className="flex gap-3 text-[12.5px]">
              <dt className="w-[70px] shrink-0 font-medium text-text">
                {t(`common:roles.${role}`)}
              </dt>
              <dd className="text-text-muted">{t(`members.roleDescription.${role}`)}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </div>
  );
}

/**
 * Invite colleagues into this workspace.
 *
 * Only rendered when the API says the caller has roles they may assign, which
 * is the same rule the server enforces on the invitation itself. A person who
 * cannot invite never sees the form rather than seeing it fail.
 *
 * Everything here is scoped to one workspace. A tenant's admins add their own
 * people; nobody outside it can, and they cannot reach anyone else's.
 */
function InvitePanel({ assignable }: { assignable: Role[] }) {
  const { t } = useTranslation(["settings", "common"]);
  const st = useServerText();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(assignable.includes("user") ? "user" : assignable[0]!);
  const [sent, setSent] = useState<string | null>(null);

  const invitations = useQuery({
    queryKey: ["invitations"],
    queryFn: api.listInvitations,
  });

  const invite = useMutation({
    mutationFn: () => api.inviteMember({ email: email.trim(), role }),
    onSuccess: (result) => {
      setSent(
        result.resent
          ? t("members.invite.resent", { email: email.trim() })
          : t("members.invite.sent", { email: email.trim() }),
      );
      setEmail("");
      setTimeout(() => setSent(null), 6000);
      void queryClient.invalidateQueries({ queryKey: ["invitations"] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeInvitation(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["invitations"] }),
  });

  const pending = invitations.data ?? [];

  return (
    <Card className="p-5">
      <h2 className="text-[14px] font-semibold text-text">{t("members.invite.title")}</h2>
      <p className="mt-1 text-[13px] leading-relaxed text-text-muted">
        {t("members.invite.body")}
      </p>

      <form
        className="mt-3.5 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) invite.mutate();
        }}
      >
        <label className="min-w-[200px] flex-1">
          <span className="mb-1.5 block text-[12.5px] font-medium text-text">{t("members.invite.email")}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("members.invite.emailPlaceholder")}
            className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-[13px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
          />
        </label>

        <label>
          <span className="mb-1.5 block text-[12.5px] font-medium text-text">{t("members.invite.role")}</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="h-9 rounded-lg border border-border bg-bg px-2.5 text-[13px] text-text focus:border-accent focus:outline-none"
          >
            {assignable.map((r) => (
              <option key={r} value={r}>
                {t(`common:roles.${r}`)}
              </option>
            ))}
          </select>
        </label>

        <Button type="submit" variant="primary" disabled={!email.trim() || invite.isPending}>
          {invite.isPending ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
          {t("members.invite.send")}
        </Button>
      </form>

      {invite.error ? (
        <p role="alert" className="mt-2.5 text-[12.5px] text-danger">
          {invite.error instanceof Error ? st(invite.error.message) : t("members.invite.failed")}
        </p>
      ) : null}

      {sent ? <p className="mt-2.5 text-[12.5px] text-success">{sent}</p> : null}

      {pending.length > 0 ? (
        <div className="mt-4 border-t border-border pt-3.5">
          <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-text-faint">
            {t("members.invite.waiting")}
          </h3>
          <div className="space-y-1.5">
            {pending.map((invitation) => (
              <div
                key={invitation.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-hover"
              >
                <div className="min-w-0">
                  <span className="text-[13px] text-text">{invitation.email}</span>
                  <span className="ml-2 text-[12px] text-text-faint">
                    {invitation.expired
                      ? t("members.invite.roleExpired", {
                          role: t(`common:roles.${invitation.role}`),
                        })
                      : t(`common:roles.${invitation.role}`)}
                  </span>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  {invitation.expired ? (
                    <Badge tone="warning">{t("members.invite.expired")}</Badge>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(invitation.id)}
                  >
                    {t("members.invite.revoke")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Card>
  );
}
